/**
 * Reward Edit Page
 *
 * Full editing capability for all reward fields including the complex
 * ui_content_json with structured form + JSON toggle.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { logger } from '@/app/utils/logger';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import { Input, Select, TextArea } from '@/components/common/FormField';
import { SelectWithAdd } from '@/components/common/FormField/SelectWithAdd';
import {
  RewardResponse,
  UpdateRewardRequest,
  WorkflowContent,
  RedemptionConfig,
  RewardConfig,
  STATUS_OPTIONS,
  RewardCategory,
  RewardStatus,
  RewardVariant,
  WorkflowType,
} from '@/app/types/rewards';
import { rewardService } from '@/app/services/rewardService';
import { auditService } from '@/app/services';
import { UIContentEditor } from '@/components/rewards/UIContentEditor';
import { RedemptionConfigEditor } from '@/components/rewards/RedemptionConfigEditor';
import { RewardConfigEditor } from '@/components/rewards/RewardConfigEditor';
import {
  useRewardMetadata,
  getCategoryOptions,
  getVariantOptions,
} from '@/app/hooks/useRewardMetadata';
import { useRewardData } from '@/app/hooks/useRewardData';

import './RewardEdit.scss';

const WORKFLOW_OPTIONS = [
  { value: '', label: 'Select workflow type' },
  { value: 'FIXED_AMOUNT', label: 'Fixed Amount' },
  { value: 'VARIABLE_AMOUNT', label: 'Variable Amount' },
];

export const RewardEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { categories, variants, loading: metadataLoading } = useRewardMetadata();
  const { invalidate, refresh } = useRewardData();

  // Local state for newly added categories/variants/providers
  const [localCategories, setLocalCategories] = useState<string[]>([]);
  const [localVariants, setLocalVariants] = useState<string[]>([]);
  const [localProviders, setLocalProviders] = useState<string[]>([]);

  // Merge fetched categories/variants with locally added ones
  const allCategories = [...categories, ...localCategories];
  const allVariants = [...variants, ...localVariants];

  const categoryOptions = getCategoryOptions(allCategories);
  const variantOptions = getVariantOptions(allVariants);

  // State for available rewards (for provider dropdown)
  const [availableRewards, setAvailableRewards] = useState<RewardResponse[]>([]);
  const [loadingRewards, setLoadingRewards] = useState(false);

  // Loading state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<UpdateRewardRequest>({});
  const [originalData, setOriginalData] = useState<RewardResponse | null>(null);

  // Fetch reward data
  useEffect(() => {
    const fetchReward = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const reward = await rewardService.getRewardById(id);
        if (!reward) {
          setError('Reward not found');
          return;
        }
        setOriginalData(reward);
        setFormData({
          reward_key: reward.reward_key,
          reward_name: reward.reward_name,
          ux_reward_title: reward.ux_reward_title,
          ux_reward_subtitle: reward.ux_reward_subtitle,
          ux_reward_description: reward.ux_reward_description,
          ux_image_url: reward.ux_image_url,
          category: reward.category,
          status: reward.status,
          point_cost: reward.point_cost,
          dollar_value: reward.dollar_value
            ? typeof reward.dollar_value === 'string'
              ? parseFloat(reward.dollar_value)
              : reward.dollar_value
            : null,
          out_of_pocket_cost: reward.out_of_pocket_cost
            ? typeof reward.out_of_pocket_cost === 'string'
              ? parseFloat(reward.out_of_pocket_cost)
              : reward.out_of_pocket_cost
            : null,
          ui_content_json: reward.ui_content_json,
          redemption_config: reward.redemption_config,
          reward_config: reward.reward_config,
          reward_variant: reward.reward_variant,
          provider_name: reward.provider_name,
        });
      } catch (err) {
        setError('Failed to load reward');
        logger.error('Failed to load reward for editing', { error: err, rewardId: id });
      } finally {
        setLoading(false);
      }
    };

    fetchReward();
  }, [id]);

  // Fetch available rewards for provider dropdown
  useEffect(() => {
    const fetchRewards = async () => {
      setLoadingRewards(true);
      try {
        const response = await rewardService.queryRewards({ limit: 100 });
        setAvailableRewards(response.rewards);
      } catch (err) {
        logger.error('Failed to fetch rewards for provider list', { error: err });
      } finally {
        setLoadingRewards(false);
      }
    };
    fetchRewards();
  }, []);

  // Derive unique providers from available rewards
  const providerOptions = useMemo(() => {
    const providersFromRewards = availableRewards
      .map((r) => r.provider_name)
      .filter((p): p is string => !!p);
    const allProviders = [...new Set([...providersFromRewards, ...localProviders])].sort();
    return allProviders.map((provider) => ({
      value: provider,
      label: provider,
    }));
  }, [availableRewards, localProviders]);

  // Handle form field changes
  const handleChange = <K extends keyof UpdateRewardRequest>(
    field: K,
    value: UpdateRewardRequest[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle UI content JSON changes
  const handleUIContentChange = (content: WorkflowContent | null) => {
    setFormData((prev) => ({ ...prev, ui_content_json: content }));
  };

  // Handle redemption config changes
  const handleRedemptionConfigChange = (config: RedemptionConfig | null) => {
    setFormData((prev) => ({ ...prev, redemption_config: config }));
  };

  // Handle reward config changes
  const handleRewardConfigChange = (config: RewardConfig | null) => {
    setFormData((prev) => ({ ...prev, reward_config: config }));
  };

  // Save changes
  const handleSave = async () => {
    if (!id) return;

    setSaving(true);
    setError(null);

    try {
      const updated = await rewardService.updateReward(id, formData);
      if (updated) {
        // Log record update for audit
        auditService
          .logRecordModification('updated', 'reward', id, {
            reward_name: updated.reward_name,
          })
          .catch((err) => logger.warn('Failed to log record update', { error: err }));

        invalidate();
        refresh(); // Force immediate refetch
        navigate(`/rewards/${id}`);
      } else {
        setError('Failed to save changes');
      }
    } catch (err) {
      setError('Failed to save changes');
      logger.error('Failed to save reward changes', { error: err, rewardId: id });
    } finally {
      setSaving(false);
    }
  };

  // Cancel editing
  const handleCancel = () => {
    navigate(`/rewards/${id}`);
  };

  if (loading) {
    return (
      <div className="reward-edit-page">
        <div className="loading-state">
          <div className="spinner" />
          <span>Loading reward...</span>
        </div>
      </div>
    );
  }

  if (error && !originalData) {
    return (
      <div className="reward-edit-page">
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <span className="error-message">{error}</span>
          <button onClick={() => navigate('/rewards')} className="btn-back">
            Back to Rewards
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reward-edit-page">
      {/* Back Navigation */}
      <div className="back-nav">
        <button onClick={handleCancel} className="btn-back-link">
          ← Cancel and go back
        </button>
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="header-actions">
          <button className="btn-cancel" onClick={handleCancel} disabled={saving}>
            Cancel
          </button>
          <button className="btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
        </div>
      )}

      {/* Basic Information */}
      <Card className="form-card">
        <CardHeader title="Basic Information" />
        <CardBody>
          <div className="form-grid">
            <Input
              label="Reward Key"
              value={formData.reward_key || ''}
              onChange={(val) => handleChange('reward_key', val)}
              placeholder="e.g., benepass-card"
              hint="Unique identifier for this reward"
            />
            <Input
              label="Internal Name"
              value={formData.reward_name || ''}
              onChange={(val) => handleChange('reward_name', val)}
              placeholder="e.g., Lore Rewards"
            />
            <SelectWithAdd
              label="Category"
              value={formData.category || ''}
              onChange={(val) => handleChange('category', val as RewardCategory)}
              options={categoryOptions.filter((o) => o.value !== '')}
              disabled={metadataLoading}
              onAddNew={(newCategory) => {
                // Add to local state if not already present
                if (!localCategories.includes(newCategory) && !categories.includes(newCategory)) {
                  setLocalCategories((prev) => [...prev, newCategory]);
                }
              }}
              addNewLabel="+ Add New Category"
              newItemPlaceholder="Enter new category name"
            />
            <Select
              label="Status"
              value={formData.status || ''}
              onChange={(val) => handleChange('status', val as RewardStatus)}
              options={STATUS_OPTIONS.filter((o) => o.value !== '').map((o) => ({
                value: o.value,
                label: o.label,
              }))}
            />
            <SelectWithAdd
              label="Variant"
              value={formData.reward_variant || ''}
              onChange={(val) => handleChange('reward_variant', val as RewardVariant)}
              options={variantOptions.filter((o) => o.value !== '')}
              disabled={metadataLoading}
              onAddNew={(newVariant) => {
                // Add to local state if not already present
                if (!localVariants.includes(newVariant) && !variants.includes(newVariant)) {
                  setLocalVariants((prev) => [...prev, newVariant]);
                }
              }}
              addNewLabel="+ Add New Variant"
              newItemPlaceholder="Enter new variant name"
            />
            <SelectWithAdd
              label="Provider Name"
              value={formData.provider_name || ''}
              onChange={(val) => handleChange('provider_name', val || null)}
              options={providerOptions}
              disabled={loadingRewards}
              onAddNew={(newProvider) => {
                const existingProviders = availableRewards
                  .map((r) => r.provider_name)
                  .filter((p): p is string => !!p);
                if (
                  !localProviders.includes(newProvider) &&
                  !existingProviders.includes(newProvider)
                ) {
                  setLocalProviders((prev) => [...prev, newProvider]);
                }
              }}
              addNewLabel="+ Add New Provider"
              newItemPlaceholder="Enter new provider name"
              hint="Provider name (auto-synced from Redemption Config if not set)"
            />
            <Select
              label="Workflow Type"
              value={formData.ui_content_json?.workflow_type || ''}
              onChange={(val) => {
                if (!val) return;
                const currentContent = formData.ui_content_json || {
                  workflow_type: 'VARIABLE_AMOUNT' as WorkflowType,
                };
                handleUIContentChange({
                  ...currentContent,
                  workflow_type: val as WorkflowType,
                });
              }}
              options={WORKFLOW_OPTIONS}
            />
          </div>
        </CardBody>
      </Card>

      {/* Pricing */}
      <Card className="form-card">
        <CardHeader title="Pricing" />
        <CardBody>
          <div className="form-grid">
            <Input
              label="Point Cost"
              value={formData.point_cost?.toString() || ''}
              onChange={(val) => handleChange('point_cost', val ? parseInt(val, 10) : 0)}
              type="number"
              hint="Cost in points to redeem"
            />
            <Input
              label="Dollar Value"
              value={formData.dollar_value?.toString() || ''}
              onChange={(val) => handleChange('dollar_value', val ? parseFloat(val) : null)}
              type="number"
              placeholder="Optional"
              hint="Dollar equivalent value"
            />
            <Input
              label="Out of Pocket Cost"
              value={formData.out_of_pocket_cost?.toString() || ''}
              onChange={(val) => handleChange('out_of_pocket_cost', val ? parseFloat(val) : null)}
              type="number"
              placeholder="Optional"
              hint="User's out of pocket cost"
            />
          </div>
        </CardBody>
      </Card>

      {/* Reward Configuration */}
      <Card className="form-card">
        <CardHeader
          title="Reward Configuration"
          subtitle="Limits, availability, pool membership, and completion points"
        />
        <CardBody>
          <RewardConfigEditor
            value={formData.reward_config || null}
            onChange={handleRewardConfigChange}
          />
        </CardBody>
      </Card>

      {/* Display Content */}
      <Card className="form-card">
        <CardHeader title="Display Content" subtitle="What users see in the app" />
        <CardBody>
          <div className="form-stack">
            <Input
              label="UX Title"
              value={formData.ux_reward_title || ''}
              onChange={(val) => handleChange('ux_reward_title', val)}
              placeholder="e.g., Transfer to Rewards Card"
            />
            <Input
              label="UX Subtitle"
              value={formData.ux_reward_subtitle || ''}
              onChange={(val) => handleChange('ux_reward_subtitle', val || null)}
              placeholder="e.g., Redeem up to $600 a year"
            />
            <TextArea
              label="UX Description"
              value={formData.ux_reward_description || ''}
              onChange={(val) => handleChange('ux_reward_description', val || null)}
              placeholder="e.g., (10 points = $1.00)"
              rows={2}
            />
            <Input
              label="Image URL"
              value={formData.ux_image_url || ''}
              onChange={(val) => handleChange('ux_image_url', val || null)}
              placeholder="e.g., reward-card.svg"
              hint="Image filename (e.g., 'reward-card.svg') or full URL. If not provided: card variants use {reward_key}.svg from /images/, row variant uses {reward_key}-icon.svg from /icons/."
            />
          </div>
        </CardBody>
      </Card>

      {/* UI Content JSON Editor */}
      <Card className="form-card ui-content-card">
        <CardHeader title="UI Configuration" subtitle="Advanced component and page configuration" />
        <CardBody>
          <UIContentEditor
            value={formData.ui_content_json || null}
            onChange={handleUIContentChange}
          />
        </CardBody>
      </Card>

      {/* Redemption Configuration */}
      <Card className="form-card">
        <CardHeader
          title="Redemption Configuration"
          subtitle="How this reward is fulfilled (manual, API, or instant)"
        />
        <CardBody>
          <RedemptionConfigEditor
            value={formData.redemption_config || null}
            onChange={handleRedemptionConfigChange}
          />
        </CardBody>
      </Card>

      {/* Bottom Actions */}
      <div className="bottom-actions">
        <button className="btn-cancel" onClick={handleCancel} disabled={saving}>
          Cancel
        </button>
        <button className="btn-save" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default RewardEdit;
